/**
 * Pagination System Type Definitions
 * Provides type-safe interfaces for the pagination system
 */

/**
 * Parameters for pagination requests
 */
export interface PaginationParams {
  page: number;
  limit: number;
  sort?: string;
  order?: 'asc' | 'desc';
}

/**
 * Pagination metadata returned from API
 */
export interface PaginationMeta {
  page: number;
  limit: number;
  totalPages: number;
  totalCount: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

/**
 * Generic paginated response wrapper
 */
export interface PaginatedResponse<T> {
  data: T[];
  pagination: PaginationMeta;
}

/**
 * Configuration for pagination hook
 */
export interface UsePaginationConfig {
  initialPage?: number;
  initialLimit?: number;
  limitOptions?: number[];
  persistState?: boolean;
  storageKey?: string;
  syncWithUrl?: boolean;
}

/**
 * Configuration for paginated query hook
 */
export interface UsePaginatedQueryConfig<T> extends UsePaginationConfig {
  queryKey: unknown[];
  queryFn: (params: PaginationParams & Record<string, any>) => Promise<PaginatedResponse<T>>;
  filters?: Record<string, any>;
  enabled?: boolean;
  prefetchNext?: boolean;
  prefetchPrevious?: boolean;
}

/**
 * Return type for pagination hook
 */
export interface PaginationState {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
  hasNext: boolean;
  hasPrevious: boolean;
  isFirstPage: boolean;
  isLastPage: boolean;
}

/**
 * Pagination actions for navigation
 */
export interface PaginationActions {
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  nextPage: () => void;
  previousPage: () => void;
  firstPage: () => void;
  lastPage: () => void;
  canGoNext: boolean;
  canGoPrevious: boolean;
}

/**
 * Combined pagination hook return type
 */
export interface UsePaginationReturn extends PaginationState, PaginationActions {}

/**
 * Default pagination configuration
 */
export const DEFAULT_PAGINATION_CONFIG = {
  initialPage: 1,
  initialLimit: 50,
  limitOptions: [10, 25, 50, 100],
  persistState: true,
  syncWithUrl: true,
};
